<template>
  <div>
    <v-btn
    @click.stop="dialog = true">
      <v-icon>mdi-cog</v-icon>
      Settings
    </v-btn>
    <v-dialog
      width="700"
      v-model="dialog">
      <v-card title="Sustainability Settings">
        <v-card-title>
          Sustainability Settings
        </v-card-title>
        <v-card-text>
          <v-select
            :items="dateSelectionOptions"
            v-model="formData.timeRange"
            :label="$t('time_frame')"/>
          <v-row v-if="customTimeRange">
            <v-col>
              <date-picker
                size="sm"
                v-model="formData.startDate"
                :timezone="customerTimezone"
                reset-on-timezone-change
                :label="$t('start_date')"
                prepend-inner-icon="mdi-calendar"
                :error-messages="dateRangeErrors"/>
            </v-col>
            <span class="text-subtitle-1 mx-2">{{ $t('to') }}</span>
            <v-col>
              <date-picker
                size="sm"
                v-model="formData.endDate"
                :timezone="customerTimezone"
                reset-on-timezone-change
                :label="$t('end_date')"
                prepend-inner-icon="mdi-calendar"
                :error="dateRangeErrors.length > 0" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                v-model="formData.dieselPrice"
                :label="$t('diesel_price')"
                :error-messages="dieselPriceErrors" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-select
                :items="currencyOptions"
                v-model="formData.currency"
                :label="$tc('currency')" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-select
                :items="dieselUnitOptions"
                v-model="formData.dieselUnit"
                :label="$t('diesel_units')" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-select
                :items="weightFormatOptions"
                v-model="formData.weightFormat"
                :label="$t('emissions_units')" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                v-model="formData.gallonsPerHour"
                :label="$t('gallons_per_hour')"
                :error-messages="gallonsPerHourErrors" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                v-model="formData.dieselRuntimeTarget"
                :label="$t('diesel_runtime_target')"
                :error-messages="dieselRuntimeTargetErrors" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                v-model="formData.stagingRuntimeTarget"
                :label="$t('shore_runtime_target')"
                :error-messages="stagingRuntimeTargetErrors" />
            </v-col>
          </v-row>
          <v-btn text size="sm" class="float-right" @click="resetFormToDefaults" variant="link">{{ $t('reset_to_defaults') }}</v-btn>
        </v-card-text>
        <v-spacer />
        <v-card-actions>
          <v-spacer></v-spacer>
            <v-btn size="sm" @click="dialog = false">{{ $t('cancel') }}</v-btn>
            <v-btn size="sm" variant="primary" @click="commitChanges">
              {{ $t('update_settings') }}
            </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DatePicker from '@/components/shared/DatePicker'
import SustainabilityMixin from '@/components/mixins/SustainabilityMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import ReportUtils from '@/components/report/ReportUtils'
import { required, decimal } from 'vuelidate/lib/validators'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCog, faPercent } from '@fortawesome/free-solid-svg-icons'
import { clone, upperFirst } from 'lodash'

library.add(faCog, faPercent)

export default {
  name: 'SustainabilitySettings',
  components: {
    DatePicker
  },
  mixins: [
    SustainabilityMixin,
    CurrentUserMixin,
    ValidationMixin
  ],
  validations: {
    formData: {
      dieselPrice: {
        required, decimal
      },
      gallonsPerHour: {
        required, decimal
      },
      dieselRuntimeTarget: {
        required
      },
      stagingRuntimeTarget: {
        required
      }
    },
    dateRange: {
      rangeIsValid: function () {
        if (this.formData.timeRange !== 'custom') {
          return true
        }
        return ReportUtils.dateRangeIsValid(this.formData.startDate, this.formData.endDate)
      }
    }
  },
  data () {
    return {
      dialog: false,
      formData: null,
      defaultParams: null,
      dateSelectionOptions: [
        {
          text: 'Last 30 days',
          value: '-30 days'
        },
        {
          text: 'Last 60 days',
          value: '-60 days'
        },
        {
          text: 'Last 90 days',
          value: '-90 days'
        },
        {
          text: 'Last 6 months',
          value: '-6 months'
        },
        {
          text: 'Last 1 year',
          value: '-1 year'
        },
        {
          text: 'Custom Range',
          value: 'custom'
        }
      ],
      currencyOptions: [
        {
          text: 'US Dollar',
          value: 'USD'
        },
        {
          text: 'Canadian Dollar',
          value: 'CAD'
        },
        {
          text: 'Euro',
          value: 'EUR'
        },
        {
          text: 'British Pound',
          value: 'GBP'
        },
        {
          text: 'Australian Dollar',
          value: 'AUD'
        },
        {
          text: 'Korean Won',
          value: 'KRW'
        }
      ],
      dieselUnitOptions: [
        {
          text: 'Liters',
          value: 'liter'
        },
        {
          text: 'US Gallon',
          value: 'us_gallon'
        },
        {
          text: 'UK Gallon',
          value: 'uk_gallon'
        }
      ],
      weightFormatOptions: [
        {
          text: 'Metric',
          value: 'metric'
        },
        {
          text: 'Imperial',
          value: 'imperial'
        }
      ]
    }
  },
  computed: {
    dateRangeErrors () {
      return this.validationErrors([{
        prop: this.$v.dateRange,
        message: this.$t('invalid_date_range')
      }])
    },
    dieselPriceErrors () {
      return this.validationErrors([{
        prop: this.$v.formData.dieselPrice,
        message: this.$t('invalid_diesel_price')
      }])
    },
    gallonsPerHourErrors () {
      return this.validationErrors([{
        prop: this.$v.formData.gallonsPerHour,
        message: this.$t('invalid_gallons_per_hour')
      }])
    },
    dieselRuntimeTargetErrors () {
      return this.validationErrors([{
        prop: this.$v.formData.dieselRuntimeTarget,
        message: this.$t('invalid_diesel_runtime_target')
      }])
    },
    stagingRuntimeTargetErrors () {
      return this.validationErrors([{
        prop: this.$v.formData.stagingRuntimeTarget,
        message: this.$t('invalid_shore_runtime_target')
      }])
    }
  },
  methods: {
    /**
     * Populate the form with a copy of the stored parameters.
     */
    populateFormData () {
      this.formData = clone(this.sustainabilityParams)
    },
    /**
     * Reset the form back to the known defaults
     */
    resetFormToDefaults () {
      this.formData = clone(this.defaultParams)
    },
    /**
     * Populate the form and display the modal.
     */
    showModal () {
      this.populateFormData()
      this.$refs['sustainability-settings-modal'].show()
    },
    /**
     * Commit a particular field to the store if it has changed from the existing values. If it changes back to the
     * default value, reset the stored value to undefined. We do this to avoid any unnecessarily lingering state.
     * (If for instance, a user resets to default but then later the customer setting for the default value changes, we
     * want the customer to be able to use that new default transparently.)
     * @param field
     */
    commitField (field) {
      const mutator = 'sustainabilityModule/set' + upperFirst(field)
      // Field changed, commit it
      if (this.formData[field] !== this.sustainabilityParams[field]) {
        // Is the field being reset to the default? Then set the stored value to undefined
        if (this.formData[field] === this.defaultParams[field]) {
          this.$store.commit(mutator, undefined)
          // Otherwise set normally
        } else {
          this.$store.commit(mutator, this.formData[field])
        }
      }
    },
    /**
     * Commit the changes to the form when it is submitted.
     */
    commitChanges () {
      this.$v.$touch()
      if (this.$v.$error) {
        return false
      }
      try {
        this.commitField('timeRange')
        this.commitField('startDate')
        this.commitField('endDate')
        this.commitField('currency')
        this.commitField('dieselPrice')
        this.commitField('dieselUnit')
        this.commitField('weightFormat')
        this.commitField('gallonsPerHour')
        this.commitField('dieselRuntimeTarget')
        this.commitField('stagingRuntimeTarget')
      } finally {
        this.dialog = false
        this.$v.$reset()
      }
    }
  },
  created () {
    this.populateFormData()
  },
  mounted () {
    this.defaultParams = {
      timeRange: this.getDefaultField('timeRange', this.customerContext),
      startDate: this.getDefaultField('startDate'),
      endDate: this.getDefaultField('endDate'),
      currency: this.getDefaultField('currency', this.customerContext),
      dieselPrice: this.getDefaultField('dieselPrice', this.customerContext),
      dieselUnit: this.getDefaultField('dieselUnit', this.customerContext),
      weightFormat: this.getDefaultField('weightFormat', this.customerContext),
      gallonsPerHour: this.getDefaultField('gallonsPerHour', this.customerContext),
      dieselRuntimeTarget: this.getDefaultField('dieselRuntimeTarget', this.customerContext),
      stagingRuntimeTarget: this.getDefaultField('stagingRuntimeTarget', this.customerContext)
    }
  },
  watch: {
    // If we switch away from a custom time range, ensure that startDate and endDate are cleared out.
    'formData.timeRange': function (newValue, oldValue) {
      if (oldValue === 'custom') {
        this.formData.startDate = null
        this.formData.endDate = null
      }
    }
  }
}
</script>

<style scoped>
.date-selection {
  /*border: 1px solid #c0d8db;*/
}
.time-range {
  background-color: #e0e3e3;
}
</style>
